import React, { useEffect, useState } from 'react';
import { renderCheckbox, resultTabButtons } from '../utils';
import { IStyle } from 'Styles';
import { ButtonsListContainer } from 'components/Button';
import NewProgressBar from './NewProgressBar';
import { useModalHandlers } from 'components/modal';
import {
  useIsErrorChecked,
  useIsPendingChecked,
  useIsProcessedChecked,
  useSelectedImageTaskId,
  useResultCount,
  useIsSelectedTaskPreview,
  useIsSelectedTaskError,
} from 'redux/hooks/imageManagementTask';
import ImageManagementTaskService from 'business/services/ImageManagementTaskService';
import { useRMDispatch } from 'redux/hooks';
import {
  setIsErrorChecked,
  setIsPendingChecked,
  setIsProcessedChecked,
} from 'redux/reducers/imageManagementTaskReducer';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { FmlxModal } from 'fmlx-common-ui';
import { ImageManagementResultDTO } from 'business/models/Models';

const TitleBar = (props: {
  progress: number;
  setProgress(val: number): any;
  results: ImageManagementResultDTO[];
}) => {
  const { progress, results, setProgress } = props;

  const [logFileText, setLogFileText] = useState<string | ArrayBuffer>();

  const selectedImageManagementTaskId = useSelectedImageTaskId();

  const { showModal, hideModal } = useModalHandlers();
  const dispatch = useRMDispatch();
  const isError = useIsErrorChecked();
  const isProcessed = useIsProcessedChecked();
  const isPending = useIsPendingChecked();
  const resultCount = useResultCount();
  const isPreview: boolean = useIsSelectedTaskPreview();
  const isErrorstate: boolean = useIsSelectedTaskError();

  useEffect(() => {
    if (logFileText && logFileText != '') {
      viewLogsOnClick();
    }
  }, [logFileText]);

  const onChangePendingCheckbox = (value) => {
    dispatch(setIsPendingChecked(!value.target.checked));
  };

  const onChangeProcessedCheckbox = (value) => {
    dispatch(setIsProcessedChecked(!value.target.checked));
  };

  const onChangeErrorCheckbox = (value) => {
    dispatch(setIsErrorChecked(!value.target.checked));
  };

  const getTaskLogs = async () => {
    const imageManagementService = new ImageManagementTaskService();
    const fileResponse = await imageManagementService.getImageManagementLogsFile(
      selectedImageManagementTaskId,
    );
    const reader = new FileReader();
    if (fileResponse.data.size === 0) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'No logs found.');
      return;
    }

    reader.onload = function (event) {
      const text = event.target.result;
      setLogFileText(text);
    };

    reader.readAsText(new Blob([fileResponse.data]));
  };

  const viewLogsOnClick = () => {
    showModal({
      body: (
        <div
          id="titlebar-logs-body"
          className="w-[99%] h-[448px] border border-solid border-gray-200 overflow-auto whitespace-pre-wrap"
        >
          {logFileText}
        </div>
      ),
      hideCloseButton: true,
      heading: 'Task Logs',
      dialogClassName: 'min-w-[1200px] m-0',
      isCancelOnly: true,
      onCancel: () => {
        setLogFileText('');
        hideModal();
      },
      noButtonText: 'Close',
      size: FmlxModal.Size.LARGE,
    });
  };

  const downloadLogsOnClick = async () => {
    const imageManagementService = new ImageManagementTaskService();
    const fileResponse = await imageManagementService.getImageManagementLogsFile(
      selectedImageManagementTaskId,
    );
    if (!fileResponse.fileName) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'No log file found.');
      return;
    }
    const blob = new Blob([fileResponse.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileResponse.fileName;
    link.click();
  };

  return (
    <div id="titlebar-container" className="flex flex-row align-center min-h-[3rem]">
      <div
        id="titlebar-title"
        className="text-[14px] ml-[10px] w-[8%]"
        style={styles.fontFamilyRobot}
      >
        Results ({resultCount})
      </div>
      <div id="titlebar-vertical-line" style={styles.verticalLine}></div>
      <div
        id="titlebar-checkbox-filter"
        className="text-[14px] ml-2 mr-2 w-[2%]"
        style={styles.fontFamilyRobot}
      >{`Filter`}</div>
      {renderCheckbox(
        isPending,
        onChangePendingCheckbox,
        false,
        'Pending',
        { marginLeft: '0.5rem', width: '7%' },
        {
          fontFamily: 'Roboto',
          fontSize: '14px',
          fontWeight: 500,
          marginLeft: '0.75rem',
        },
      )}
      {renderCheckbox(
        isProcessed,
        onChangeProcessedCheckbox,
        false,
        'Processed',
        { marginLeft: '0.5rem', width: '8%' },
        {
          fontFamily: 'Roboto',
          fontSize: '14px',
          fontWeight: 400,
          marginLeft: '0.75rem',
        },
      )}
      {renderCheckbox(
        isError,
        onChangeErrorCheckbox,
        false,
        'Error',
        { marginLeft: '0.5rem', width: '4%' },
        {
          fontFamily: 'Roboto',
          fontSize: '14px',
          fontWeight: 400,
          marginLeft: '0.75rem',
        },
      )}
      <NewProgressBar
        mainContainerClassName="flex flex-row align-center justify-around ml-[18px] mr-[1%]  w-[33%]"
        title="Progress"
        progressBarContainerClass="taskProgress w-[354px] !h-2 ml-[8px] mr-[8px]"
        currentProgress={progress}
        progressWidth={progress}
        progressContainerStyle={styles.progressBar}
        titleClassName="text-[12px]"
        backgroundColor={isErrorstate ? 'red' : isPreview ? '#006AF5' : '#4CAF50'}
      />
      <ButtonsListContainer
        containerStyleClass="flex flex-row justify-between align-center h-[100%] w-[31%]"
        containerStyle={styles.buttonList}
        buttonsList={resultTabButtons(
          getTaskLogs,
          downloadLogsOnClick,
          setProgress,
          results,
        )}
      />
    </div>
  );
};

const styles: IStyle = {
  verticalLine: {
    borderLeft: '1px solid #DEE2E6',
    height: '2rem',
  },
  progressBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
    color: '#fff',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    transition: 'width .6s ease',
  },

  fontFamilyRobot: {
    fontFamily: 'Roboto',
  },
};

export default TitleBar;
